/**
 * How this particular workstation reads codes.
 *
 * A device setting, not an account setting: the same person uses a phone in the
 * aisles and a PC with a gun on it at the desk, and what is true of one is
 * wrong for the other. So it lives in this browser's storage and is never sent
 * anywhere.
 */

export type ScannerMode = 'keyboard' | 'serial' | 'off';

export const SCANNER_MODES: readonly ScannerMode[] = ['keyboard', 'serial', 'off'];

const MODE_KEY = 'storageapp.scanner.mode';
const BAUD_KEY = 'storageapp.scanner.baud';

export interface ScannerSettings {
  mode: ScannerMode;
  baudRate: number;
}

function read(): ScannerSettings {
  try {
    const mode = localStorage.getItem(MODE_KEY);
    const baud = Number(localStorage.getItem(BAUD_KEY));
    return {
      // Keyboard by default because a gun in that mode needs no permission, no
      // driver and no choice made in advance — it simply works.
      mode: mode === 'serial' || mode === 'off' ? mode : 'keyboard',
      baudRate: Number.isFinite(baud) && baud > 0 ? baud : 9600,
    };
  } catch {
    return { mode: 'keyboard', baudRate: 9600 };
  }
}

let settings = read();
const listeners = new Set<() => void>();

export function getScannerSettings(): ScannerSettings {
  return settings;
}

export function setScannerSettings(next: Partial<ScannerSettings>): void {
  settings = { ...settings, ...next };
  try {
    localStorage.setItem(MODE_KEY, settings.mode);
    localStorage.setItem(BAUD_KEY, String(settings.baudRate));
  } catch {
    // Applies to this session; just will not be remembered.
  }
  listeners.forEach((notify) => notify());
}

export function subscribeScannerSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
